import { type ComponentProps, useState } from 'react';

import { Box, Typography } from '@mui/material';
import { Image } from '@realadvisor/image';
import { ImageOff } from 'lucide-react';

import { useLocale } from '@shared/hooks/locale';

type Props = ComponentProps<typeof Image> & {
  /** Icon-only, for slots too small to fit the label (list thumbnails). */
  compact?: boolean;
};

// `Image` tolerates a nullish src in production; the wrapper must not be stricter.
const srcKey = (src: ComponentProps<typeof Image>['src']) =>
  src == null
    ? ''
    : typeof src === 'string'
    ? src
    : `${src.bucketName}/${src.fileName}`;

/**
 * `Image` with a visible failure state. `@realadvisor/image` forwards `onError` to the
 * `<img>` but ships no fallback, so an undecodable source otherwise renders as an empty
 * box with nothing said. Not `NoPhotosPlaceholder`: it says "No photo".
 */
export const GalleryImage = ({ compact = false, ...props }: Props) => {
  const { t } = useLocale();
  const [hasError, setHasError] = useState(false);

  // Without this the tile stays stuck on the failure state for an image that now loads.
  // Reset during render rather than in an effect: an effect runs after commit, so the
  // first render with a new source would repaint the failure tile for one frame on a
  // tile that now works. Keyed on a string because `src` may be an object rebuilt on
  // every render.
  const key = srcKey(props.src);
  const [prevKey, setPrevKey] = useState(key);
  if (key !== prevKey) {
    setPrevKey(key);
    setHasError(false);
  }

  // No source is not a failed source. With an empty src `Image` still emits a srcset,
  // whose candidates are then empty relative URLs that resolve against the document; the
  // fetch fails and `onError` fires, which would otherwise label an absent photo a failed
  // one. The failure was observed; this is the reading of `packages/image` that explains it.
  if (key === '') {
    return null;
  }

  if (hasError) {
    return (
      // The caller's `css` arrives as `className`: emotion's JSX runtime serializes it
      // before the component is called. That is what sizes and positions the fallback
      // exactly like the image it replaces.
      <Box
        className={props.className}
        // The image is gone; without these a screen reader gets nothing where it was,
        // and in compact mode there is no text node either.
        role="img"
        aria-label={t('Failed to load image')}
        title={t('Failed to load image')}
        // `grey.*` is scheme-aware in this theme: `StyleProvider` inverts the ramp for dark
        // mode, so `grey.200` is a dark surface there and `grey.700` stays readable text.
        // No dark branch needed, and `grey.500` did not clear 4.5:1 against `grey.200`.
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          p: 1,
          color: 'grey.700',
          backgroundColor: 'grey.200',
        }}
      >
        <ImageOff size={compact ? 20 : 28} />
        {!compact && (
          <Typography variant="body2">{t('Failed to load image')}</Typography>
        )}
      </Box>
    );
  }

  return (
    <Image
      {...props}
      onError={() => {
        setHasError(true);
        props.onError?.();
      }}
    />
  );
};
